import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable, of } from 'rxjs';
import { FileObject, FileUploadService, IPreparedFile } from '../../../services/file-upload.service';

export interface LoadedFile {
  id: string;
  fileObject: FileObject;
}

type AppMediaFileForm = {
  id: FormControl<number | null>;
  file: FormControl<File | null>;
  url: FormControl<string | null>;
  name: FormControl<string | null>;
};

type AppMediasForm = {
  fileControls: FormArray<FormGroup<AppMediaFileForm>>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-medias-for-app',
  templateUrl: './add-medias-for-app.component.html',
  styleUrls: ['./add-medias-for-app.component.scss']
})
export class AddMediasForAppComponent implements OnInit {

isLoading = false;
  files: IPreparedFile[] = [];

  form = new FormGroup<AppMediasForm>({
    fileControls: new FormArray<FormGroup<AppMediaFileForm>>([])
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      files: IPreparedFile[];
    },
    private fileUpload: FileUploadService,
    private dialogRef: MatDialogRef<AddMediasForAppComponent, { files: IPreparedFile[] }>,
  ) {}

  ngOnInit(): void {
    this.addFilesFromData();
  }

  get fileControls(): FormArray<FormGroup<AppMediaFileForm>> {
    return this.form.controls.fileControls;
  }

  addNewFileControl(): void {
    const isFirstControl = this.fileControls.length === 0;

    const group = new FormGroup<AppMediaFileForm>({
      id: new FormControl<number | null>(null),
      file: new FormControl<File | null>(null, isFirstControl ? [Validators.required] : []),
      url: new FormControl<string | null>(null),
      name: new FormControl<string | null>(null)
    });

    this.fileControls.push(group);

    this.updateFileValidators();
  }

  addImageControl(file: IPreparedFile): void {
    const group = new FormGroup<AppMediaFileForm>({
      id: new FormControl<number | null>(file.id || null),
      file: new FormControl<File | null>(null),
      url: new FormControl<string | null>(file.url || null),
      name: new FormControl<string | null>(file.name || null)
    }, { validators: [] });

    this.fileControls.push(group);
  }

  updateFileValidators(): void {
    const isRequired = this.fileControls.length === 1;

    this.fileControls.controls.forEach((group) => {
      const fileControl = group.controls.file;
      if (isRequired) {
        fileControl?.setValidators([Validators.required]);
      } else {
        fileControl?.clearValidators();
      }
      fileControl?.updateValueAndValidity();
    });
  }

  addFilesFromData(): void {
    if (!this.data.files?.length) {
      return;
    }

    this.data.files.forEach((fileObject) => {
      this.addImageControl(fileObject);
    });
  }

  processFilesForForm(): Observable<{files: IPreparedFile[]}> {
    const fileGroups = this.fileControls.getRawValue();

    const files: File[] = fileGroups
      .map(group => group.file)
      .filter((file): file is File => file instanceof File);

    if (files.length === 0) {
      return of({ files: [] });
    }

    const uploadObservables: Observable<IPreparedFile>[] = files.map(file =>
      this.fileUpload.upload(file).pipe(
        map(event => event.intent.fileId),
        switchMap((fileId: number) => this.fileUpload.getFileById(fileId)),
        map((fileObject: FileObject) => ({
          id: fileObject.id,
          url: fileObject.url,
          name: fileObject.name
        })),
        catchError(err => {
          console.error('File process error', err);
          return of(null);
        }),
        filter((result): result is IPreparedFile => result !== null)
      )
    );

    return forkJoin(uploadObservables).pipe(
      map(results => ({ files: results }))
    );
  }

  getExistingFiles(): IPreparedFile[] {
    return this.fileControls.controls
      .filter((group) => {
        const val = group.getRawValue();
        return !val.file && val.url && val.id;
      })
      .map((group) => {
        const value = group.getRawValue();
        const file = {
          id: value.id,
          url: value.url,
          name: value.name
        };
        return file as IPreparedFile;
      });
  }

  removeFileControl(index: number): void {
    this.fileControls.removeAt(index);
    this.fileControls.markAsDirty();
    this.updateFileValidators();
  }

  get isAddAllowed(): boolean {
    return !this.fileControls.controls.every(ctrl => {
      const value = ctrl.getRawValue();
      return !!value.id || value.file !== null;
    });
  }

  public onSubmit(): void {
    this.isLoading = true;

    this.processFilesForForm().pipe(
      untilDestroyed(this)
    ).subscribe({
      next: ({ files: newFiles }) => {
        const existingFiles = this.getExistingFiles();

        this.dialogRef.close({
          files: [...existingFiles, ...newFiles]
        });
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Upload failed', err);
        this.isLoading = false;
      }
    });
  }
}
